import { motion, useReducedMotion } from 'framer-motion';
import { useContext, useEffect, useState } from 'react';
import Countdown from 'react-countdown';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { TimesyncContext } from '../../../providers/TimesyncProvider/TimesyncProvider';
import { zIndexes } from '../../../utils/zIndexes';

// SimpleLiveGameWidgetV2 sets roundStartsAt this far ahead when an activity
// starts, so roundStartsAt - lead is when the countdown began.
export const ROUND_COUNTDOWN_LEAD_MS = 3700;
// 3 and 2 get one second each from the start; 1 holds the remaining 1.7 s,
// the last part of which fades the overlay out into the activity.
const TICK_MS = 1000;
const FADE_OUT_MS = 400;

const getRemainingMs = (date: Date, now: () => number) =>
  Math.max(0, date.getTime() - now());

export const getNumber = (remainingMs: number) => {
  const elapsedMs = Math.max(0, ROUND_COUNTDOWN_LEAD_MS - remainingMs);
  return Math.max(1, 3 - Math.floor(elapsedMs / TICK_MS));
};

/**
 * Pure-black full-screen 3 · 2 · 1 countdown before any activity round
 * starts. Each number animates like the activity timer's red final seconds
 * (LiveCountdown with `animate`): it scales up from 30% and fades in over
 * 0.6 s while the old one is simply replaced. The 1 stays until the round
 * starts, and the overlay fades out over the activity as it does.
 */
const NumberCountdownOverlay = ({
  date,
  onComplete,
}: {
  date: Date;
  onComplete: () => void;
}) => {
  const { now } = useContext(TimesyncContext);
  const isReducedMotion = useReducedMotion();
  const [remainingMs, setRemainingMs] = useState(() => getRemainingMs(date, now));

  useEffect(() => {
    const ref = setInterval(() => setRemainingMs(getRemainingMs(date, now)), 50);
    return () => clearInterval(ref);
  }, [date, now]);

  const number = getNumber(remainingMs);
  const isFadingOut = remainingMs <= FADE_OUT_MS;

  return (
    <Overlay
      initial={false}
      animate={{ opacity: isFadingOut ? 0 : 1 }}
      transition={{ ease: 'easeInOut', duration: FADE_OUT_MS / 1000 }}
    >
      <CountdownNumber
        key={number}
        initial={isReducedMotion ? { opacity: 0 } : { scale: 0.3, opacity: 0 }}
        animate={isReducedMotion ? { opacity: 1 } : { scale: 1, opacity: 1 }}
        transition={{ ease: 'easeInOut', duration: 0.6 }}
      >
        {number}
      </CountdownNumber>
      <Countdown now={now} date={date} renderer={() => null} onComplete={onComplete} />
    </Overlay>
  );
};

// Fixed to the viewport so it fills the whole screen (letterbox included),
// stacked like the black voting-title screen that follows it.
const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${theme.colors.blackPure};
  z-index: ${zIndexes('AI_FEEDBACK_OVERLAY')};
`;

const CountdownNumber = styled(motion.span)`
  color: ${theme.colors.white};
  font-size: clamp(96px, 38vh, 300px);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
`;

export default NumberCountdownOverlay;
